import { Eye, Layers, ShieldCheck, UserRound, Image, FolderHeart, ListVideo, Rocket, MessageCircle, BarChart3, Compass, Heart, Repeat, Unlock, Library, HandHeart } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useSmoothScroll } from "./SmoothScroll";
import { Reveal, SectionTitle } from "./Primitives";

const pillarIcons = [Eye, Layers, ShieldCheck];
const creatorIcons = [UserRound, Image, FolderHeart, ListVideo, Rocket, MessageCircle, BarChart3];
const fanIcons = [Compass, Heart, Repeat, Unlock, Library, HandHeart];

export const WhatIsGoox = () => {
  const { t } = useLang();
  return (
    <section id="what" data-testid="what-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionTitle eyebrow={t.what.eyebrow} title={t.what.title} sub={t.what.body} testId="what-title" />
        </div>
        <div className="grid gap-4 lg:col-span-7 lg:mt-6">
          {t.what.pillars.map((p, i) => {
            const Icon = pillarIcons[i];
            return (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="card-soft flex gap-5 p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700"><Icon size={20} /></span>
                  <div>
                    <p className="font-display text-lg font-bold text-ink">{p.t}</p>
                    <p className="mt-1 text-sm leading-relaxed text-ink-2">{p.d}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const CreatorFeatures = () => {
  const { t } = useLang();
  const { scrollTo } = useSmoothScroll();
  return (
    <section id="creators" data-testid="creators-section" className="bg-canvas py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle eyebrow={t.creators.eyebrow} title={t.creators.title} sub={t.creators.sub} testId="creators-title" />
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {t.creators.items.map((it, i) => {
            const Icon = creatorIcons[i];
            const span = i === 0 ? "lg:col-span-2 lg:row-span-2" : "";
            return (
              <Reveal key={it.t} delay={(i % 4) * 0.07} className={span}>
                <div className={`card-soft h-full p-6 ${i === 0 ? "bg-night text-white lg:p-8" : ""}`} style={i === 0 ? { background: "#12091f", borderColor: "#2a1745" } : {}}>
                  <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${i === 0 ? "bg-white/10 text-pink-300" : "bg-violet-100 text-violet-700"}`}><Icon size={20} /></span>
                  <p className={`font-display mt-6 font-bold ${i === 0 ? "text-2xl lg:text-3xl" : "text-lg"} ${i === 0 ? "text-white" : "text-ink"}`}>{it.t}</p>
                  <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-violet-200/80" : "text-ink-2"}`}>{it.d}</p>
                </div>
              </Reveal>
            );
          })}
          <Reveal delay={0.2}>
            <button onClick={() => scrollTo("#waitlist")} data-testid="creators-cta" className="card-soft flex h-full min-h-[10rem] w-full flex-col items-start justify-between p-6 text-left hover:border-violet-400">
              <span className="eyebrow text-violet-600">GOOX Studio</span>
              <span className="font-display text-xl font-bold text-ink">{t.creators.cta} →</span>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const FanFeatures = () => {
  const { t } = useLang();
  return (
    <section id="fans" data-testid="fans-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="order-2 grid gap-3 sm:grid-cols-2 lg:order-1 lg:col-span-7">
          {t.fans.items.map((it, i) => {
            const Icon = fanIcons[i];
            return (
              <Reveal key={it.t} delay={i * 0.06}>
                <div className="card-soft flex items-start gap-4 p-5">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-pink-100 text-pink-600"><Icon size={17} /></span>
                  <div>
                    <p className="font-display font-bold text-ink">{it.t}</p>
                    <p className="mt-1 text-sm text-ink-2">{it.d}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="order-1 lg:order-2 lg:col-span-5">
          <SectionTitle eyebrow={t.fans.eyebrow} title={t.fans.title} sub={t.fans.sub} testId="fans-title" />
          <Reveal delay={0.2}>
            <Link to="/#waitlist" data-testid="fans-cta" className="pill btn-primary mt-8 inline-flex px-6 py-3 text-sm font-semibold">{t.fans.cta}</Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
